<template>
	<div>
		<!-- School Mapping Button -->
		<v-tooltip bottom>
			<template v-slot:activator="{ props: tooltipProps }">
				<v-btn
					icon
					color="primary"
					size="small"
					rounded="lg"
					@click="showSchoolMapping = true"
					class="mapping-btn"
					v-bind="tooltipProps"
				>
					<v-icon>mdi-school</v-icon>
				</v-btn>
			</template>
			<span>Show school associations</span>
		</v-tooltip>

		<!-- School Mapping Dialog -->
		<v-dialog v-model="showSchoolMapping" max-width="900px">
			<v-card>
				<v-card-title class="d-flex align-center justify-space-between">
					<span>Schools Mapping</span>
					<v-tooltip bottom>
						<template v-slot:activator="{ props: tooltipProps }">
							<v-btn
								icon
								:loading="loading"
								@click="fetchOneRosterSchools"
								class="reload-btn"
								title="Reload"
								size="small"
								rounded="lg"
								color="grey lighten-1"
								v-bind="tooltipProps"
							>
								<v-icon size="18">mdi-refresh</v-icon>
							</v-btn>
						</template>
						<span>Reload the list of schools</span>
					</v-tooltip>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text>
					<div class="oneroster-table-container">
						<table class="oneroster-table">
							<thead>
								<tr>
									<th class="header-cell">OneRoster School Name</th>
									<th class="header-cell">PTC Wizard School Name</th>
									<th class="header-cell actions-header">Create school</th>
								</tr>
							</thead>
						</table>
						<div class="table-body-scroll">
							<table class="oneroster-table">
								<tbody>
									<tr v-for="row in oneroster_schools" :key="row.sourced_id">
										<td>{{ row.name }}</td>
										<td style="min-width: 250px">
											<v-autocomplete
												v-model="row.school_id"
												:items="schools"
												item-title="school_name"
												item-value="school_id"
												density="comfortable"
												clearable
												placeholder="Select school..."
											/>
										</td>
										<td class="actions-header">
											<v-btn
												color="primary"
												size="small"
												icon="mdi-database-plus"
												@click="createSchoolFromOneRoster(row)"
												:loading="row.creating"
												v-if="!row.school_id"
											>
											</v-btn>
										</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>
				</v-card-text>
				<v-card-actions>
					<v-spacer />
					<v-btn color="grey lighten-1" @click="showSchoolMapping = false">Cancel</v-btn>
					<v-btn color="success" :loading="saving" @click="saveAndClose">Save</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</div>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import { useDistrictStore } from "@/stores/district";
import { useSchoolStore } from "@/stores/school";
import FetchService from "@/services/fetch.service";

interface OneRosterSchool {
	school_id: number | null;
	name: string;
	sourced_id: string;
	creating?: boolean;
}

interface Props {
	integration_type?: string;
}

const props = withDefaults(defineProps<Props>(), {
	integration_type: "1.1",
});

const districtStore = useDistrictStore();
const schoolStore = useSchoolStore();
const showSchoolMapping = ref(false);
const schools = ref<{ school_id: number; school_name: string }[]>([]);
const oneroster_schools = ref<OneRosterSchool[]>([]);
const saving = ref(false);
const loading = ref(false);

const fetchSchools = async () => {
	await districtStore.fetchSchools();
	if (props.integration_type === "nycdoe") {
		console.log("Fetching schools for NYC DOE version");
		if (!schoolStore.school) {
			console.warn("No school data available in store");
			return [];
		} else {
			schools.value = [
				{
					school_id: schoolStore.school.school_id,
					school_name: schoolStore.school.school_name,
				},
			];
		}
	} else {
		if (!Array.isArray(districtStore.schools)) {
			return [];
		}
		schools.value = districtStore.schools
			.filter((s) => s && s.school_id != null && s.school_name != null)
			.map((s) => ({
				school_id: Number(s.school_id),
				school_name: String(s.school_name),
			}));
	}
};

const fetchOneRosterSchools = async () => {
	loading.value = true;
	const res = await FetchService.fetchData("/oneroster/schools", "get");
	oneroster_schools.value = Array.isArray(res) ? res : [];
	loading.value = false;
};

const createSchoolFromOneRoster = async (row: OneRosterSchool) => {
	row.creating = true;
	console.log("Creating school from OneRoster data", row);
	try {
		const res = await FetchService.fetchData(`/oneroster/schools/${row.sourced_id}`, "post", {
			name: row.name,
			oneroster_school_id: row.sourced_id,
		});
		if (res) {
			// Force refresh of district store schools data
			await fetchOneRosterSchools();
			await fetchSchools();
		}
	} finally {
		row.creating = false;
	}
};

const save = async () => {
	saving.value = true;
	try {
		await FetchService.fetchData("/oneroster/schools", "put", {
			schools: oneroster_schools.value,
		});
	} finally {
		saving.value = false;
	}
};

const saveAndClose = async () => {
	await save();
	showSchoolMapping.value = false;
};

// Fetch schools when dialog opens
watch(showSchoolMapping, async (newVal) => {
	if (newVal) {
		await fetchSchools();
		await fetchOneRosterSchools();
	}
});
</script>

<style scoped>
.oneroster-table-container {
	width: 100%;
}
.oneroster-table {
	table-layout: fixed;
	width: 100%;
}
.table-body-scroll {
	height: 50vh;
	max-height: 50vh;
	overflow-y: auto;
}
.header-cell {
	background: #e5e4db;
	color: #222e3a;
	font-weight: 600;
	font-size: 1rem;
	padding: 3px 3px 3px 3px;
	border-bottom: 2px solid #e3e7ee;
	letter-spacing: 0.04em;
	text-align: left;
	vertical-align: middle;
}
.actions-header {
	text-align: center;
	width: 100px;
}
.reload-btn {
	margin-left: 8px;
	min-width: 32px;
	height: 32px;
	padding: 0;
}
.mapping-btn {
	margin-left: 8px;
}
</style>

